<script setup>
import { reactive, computed, watch } from "vue";
import ButtonComponent from "../../../common/ui/ButtonComponent.vue";
import AtomIcon from "../../../common/ui/Icon.vue";
import InputText from "../../../common/ui/InputText.vue";
import InputPassword from "../../../common/ui/InputPassword.vue";
import PageHeader from "../../../common/components/page/PageHeader";
import { booleanHook } from "../../../common/hooks/booleanHook";
import { helpers, required, email, sameAs } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import ComponentInput from "../../../common/components/ComponentInput.vue";
import useUserProfile from "../../../common/hooks/useUserProfile.js";
import Constants from "@/common/constants";
import store from "@/store";

const {
  isLoading,  
  userProfile,
  loadUserProfile: loadUserProfile,
  changePassword: changeUserPassword,
  changeEmail: changeUserEmail,
} = useUserProfile();

const showAvailableRights = booleanHook();

let initialEmail = userProfile.value.email;

const emailForm = reactive({ email: userProfile.value.email });

watch(
  () => userProfile.value.email,
  () => {
    Object.assign(emailForm, { email: userProfile.value.email });
  }  
);

watch(
  () => store.state.auth?.oidc?.user && localStorage.getItem('token'),
  async () => {
    await loadUserProfile();
  }
);

const emailFormRules = computed(() => {
  return {
    email: {
      required: helpers.withMessage(
        "Электронная почта обязательна для заполнения",
        required
      ),
      email: helpers.withMessage("Не верный формат электронной почты", email),
    },
  };
});
const emailValidator = useVuelidate(emailFormRules, emailForm);

const initialPasswordForm = {
  oldPassword: "",
  newPassword: "",
  repeatNewPassword: "",
};

const passwordForm = reactive({ ...initialPasswordForm });

const passwordFormRules = computed(() => ({
  oldPassword: {
    required: helpers.withMessage(
      "Для смены пароля необходимо ввести текущий пароль",
      required
    ),    
  },
  newPassword: {
    required: helpers.withMessage("Пароль обязателен для заполнения", required),
    notSameAs: helpers.withMessage(
      "Старый и новый пароли совпадают",      
       function() {        
         return passwordForm.oldPassword != passwordForm.newPassword;
       }
    ),
  },
  repeatNewPassword: {
    required: helpers.withMessage(
      "Необходимо обязательно повторить пароль",
      required
    ),
    sameAs: helpers.withMessage(
      "Введенные пароли не совпадают",
      sameAs(passwordForm.newPassword)
    ),
  },
}));
const passwordValidator = useVuelidate(passwordFormRules, passwordForm);

const changePassword = async () => {
  const isValid = await passwordValidator.value.$validate();
  if (isValid && passwordValidator.value.$dirty) {
    try {
      const res = await changeUserPassword({
        password: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword,
      });
      if (res === true || res?.succeeded) {
        Constants.alert.fire("Сохранение", "Пароль успешно изменён", "success");
      } else {
        Constants.alert.fire(
          "Сохранение",
          res.errors
            .map((error) => ({
              ...error,
              description:
                Constants.passwordValidationErrorsRU.find(
                  ({ code }) => code === error.code
                )?.descriptionRU ?? error.description,
            }))
            .map(({ description }) => description)
            .filter((value, index, self) => self.indexOf(value) === index)
            .join("<br/>"),
          "error"
        );
      }
    } catch (e) {
      console.log(e);
      Constants.alert.fire(
        "Сохранение",
        "При изменении пароля произошла ошибка на сервере",
        "error"
      );
    }
  }
};

const changeEmail = async () => {
  const isValid = await emailValidator.value.$validate();
  if (
    isValid &&
    emailValidator.value.$dirty &&
    emailForm.email !== initialEmail
  ) {
    try {
      await changeUserEmail({
        email: emailForm.email,
      });
      Constants.alert.fire(
        "Сохранение",
        "Электронная почта успешно изменена",
        "success"
      );
      initialEmail = emailForm.email;
    } catch (e) {
      console.log(e);
      Constants.alert.fire(
        "Сохранение",
        "Произошла ошибка изменения электронной почты",
        "error"
      );
    }
  }
};
</script>

<template>
  <div>
    <page-header> <h2>Личный кабинет пользователя</h2> </page-header>
    <div v-if="isLoading">Данные загружаются...</div>
    <div v-if="!isLoading" class="user-profile">
      <div>
        <ComponentInput
          :modelValue="userProfile.surname"
          label="Фамилия:"
          disabled
        />
        <ComponentInput :modelValue="userProfile.name" label="Имя:" disabled />
        <ComponentInput
          label="Отчество:"
          disabled
          :modelValue="userProfile.patronymic"
        />
        <ComponentInput
          :modelValue="userProfile.organizationName ? userProfile.organizationName[0] : ''"
          label="Организация:"
          disabled
        />
        <InputText
          v-model="emailForm.email"
          label="Email:"
          :error="emailValidator?.email?.$error"
          :errors="emailValidator?.email?.$errors"
          :hasEditPencil="true"
        />
        <ComponentInput
          :modelValue="userProfile.roleNames?.join(', ')"
          label="Роль:"
          disabled
        />
        <div class="available-rights">
          <span
            class="available-rights__toggler"
            @click="showAvailableRights.toggle"
          >
            Доступные права
            <AtomIcon
              name="chevron"
              :class="{ 'icon--rotated': showAvailableRights.flag.value }"
            />
          </span>
          <div v-if="showAvailableRights.flag.value">
            <ul>
              <li v-for="{ id, name } in userProfile.permissions" :key="id">
                {{ name }}
              </li>
            </ul>
          </div>
        </div>
        <button-component class="user-profile__button" @click="changeEmail">
          Сохранить
        </button-component>
      </div>
      <div class="password-form__fields">
        <InputPassword
          id="oldPassword"
          v-model="passwordForm.oldPassword"
          class="password-form__field"
          name="oldPassword"
          placeholder="Старый пароль"
          label="Старый пароль"
          :error="passwordValidator?.oldPassword?.$error"
          :errors="passwordValidator?.oldPassword?.$errors"
        />
        <InputPassword
          id="newPassword"
          v-model="passwordForm.newPassword"
          class="password-form__field"
          name="newPassword"
          placeholder="Новый пароль"
          label="Новый пароль"
          :error="passwordValidator?.newPassword?.$error"
          :errors="passwordValidator?.newPassword?.$errors"
        />
        <InputPassword
          id="repeatNewPassword"
          v-model="passwordForm.repeatNewPassword"
          class="password-form__field"
          name="repeatNewPassword"
          placeholder="Повторите пароль"
          label="Повторите пароль"
          :error="passwordValidator?.repeatNewPassword?.$error"
          :errors="passwordValidator?.repeatNewPassword?.$errors"
        />
        <button-component class="user-profile__button" @click="changePassword">
          Изменить пароль
        </button-component>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "/src/assets/theme/default/colors.scss";
.user-profile {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  padding: 1rem;
  width: 50%;
  &__settings {
    margin: 0;
    padding: 0;
  }
  &__button {
    margin-top: 0.5rem;
  }
  &__setting {
    padding: 0.5rem 0;
  }
}
.available-rights {
  &__toggler {
    cursor: pointer;
  }
}
.password-form {
  &__toggler {
    cursor: pointer;
  }
}
:deep(.icon) {
  #curves {
    fill: $gray-medium;
  }
  &--rotated {
    transform: rotate(180deg);
  }
}
</style>
